import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Link, Typography } from '@mui/material'

import { selectLocale } from '@/app/slices/appSlice'
import { selectOrganisation } from '@/app/slices/organisationSlice'
import { DateTime } from '@/common/components'
import { useProductDetails, useToolBoxTreatments } from '@/common/hooks'
import { LIBELLULA_PRICING_REDIRECT_URL, TOOLBOX_PRICING_REDIRECT_URL } from '@/common/utils'

const ActivateSubscriptionBar = () => {
    const { t } = useTranslation()

    const organisation = useSelector(selectOrganisation)
    const locale = useSelector(selectLocale)
    const productDetails = useProductDetails()
    const { showLibellulaWhitelabelling } = useToolBoxTreatments()

    // Determine link destination and behavior based on environment
    const linkHref = showLibellulaWhitelabelling ? LIBELLULA_PRICING_REDIRECT_URL : TOOLBOX_PRICING_REDIRECT_URL

    const linkText = showLibellulaWhitelabelling ? t('request pricing') : t('subscribe here')

    // Only ever render a date for a live trial — never fall back to "now" for an expired/missing one.
    const trialEndDateUtc = organisation?.trial?.hasExpired ? undefined : organisation?.trial?.endDateUtc

    return (
        <Box
            data-testid="activate-subscription-bar"
            sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 2,
            }}
        >
            <Typography
                color="error"
                data-testid="activate-subscription-bar-title"
                variant="body1"
            >
                {trialEndDateUtc ? (
                    <>
                        {t("You're using a trial, ending")}{' '}
                        <DateTime
                            format="DD-MMM-YYYY"
                            locale={locale}
                        >
                            {trialEndDateUtc}
                        </DateTime>
                        {'. '}
                        {t('To continue using {{title}} after the trial period', { title: productDetails?.title })}
                    </>
                ) : (
                    t('Your trial has ended') +
                    '. ' +
                    t('To continue using {{title}}', { title: productDetails?.title })
                )}{' '}
                {showLibellulaWhitelabelling ? (
                    <Link
                        color="secondary"
                        data-testid="activate-subscription-bar-link"
                        href={linkHref}
                        target="_blank"
                    >
                        {linkText}
                    </Link>
                ) : (
                    <Link
                        color="secondary"
                        component={RouterLink}
                        data-testid="activate-subscription-bar-link"
                        to={linkHref}
                    >
                        {linkText}
                    </Link>
                )}
            </Typography>
        </Box>
    )
}

export default ActivateSubscriptionBar
